import {
  LoadAssetContainerAsync,
  Mesh,
  Scene,
  TransformNode,
  Vector3
} from "../../app/babylon";
import "@babylonjs/loaders/glTF";
import { assets } from "../assets";
import type { Slope } from "./Slope";

const TREE_SCALE_MIN = 0.9;
const TREE_SCALE_MAX = 1.6;
const TREE_COLLISION_RADIUS = 0.6;
const TREE_START_Z = 20;
const TREE_END_BUFFER = 30;

interface TreePlacement {
  position: Vector3;
  radius: number;
}

export class Trees {
  readonly placements: TreePlacement[] = [];
  readonly root: TransformNode;
  templates: Mesh[] = [];
  private templateMinY = 0;

  constructor(private readonly scene: Scene) {
    this.root = new TransformNode("trees-root", scene);
  }

  async load(slope: Slope): Promise<void> {
    const container = await LoadAssetContainerAsync(assets.treeGlb, this.scene);
    container.addAllToScene();

    for (const mesh of container.meshes) {
      if (!(mesh instanceof Mesh)) continue;
      if (mesh.getTotalVertices() === 0) continue;
      mesh.isVisible = false;
      mesh.parent = null;
      this.templates.push(mesh);
    }

    if (this.templates.length === 0) {
      console.warn("[trees] no mesh templates found in tree.glb");
      return;
    }

    const bounds = this.computeTemplateBounds();
    this.templateMinY = bounds.minY;
    const desiredHeight = 4.5;
    const baseScale = desiredHeight / Math.max(bounds.height, 0.001);

    const rand = mulberry32(1337);
    const maxZ = slope.length - TREE_END_BUFFER;

    for (let z = TREE_START_Z; z < maxZ; z += 4.5 + rand() * 3) {
      const sample = slope.sampleAtZ(z);

      const sideOffset =
        slope.groomedHalfWidth +
        1 +
        rand() * (slope.powderHalfWidth - slope.groomedHalfWidth - 1);
      this.spawnInstance(
        sample.centerX + sample.rightX * sideOffset,
        sample.surfaceY,
        z + sample.rightZ * sideOffset,
        baseScale,
        rand
      );

      const sideOffset2 =
        slope.groomedHalfWidth +
        1 +
        rand() * (slope.powderHalfWidth - slope.groomedHalfWidth - 1);
      this.spawnInstance(
        sample.centerX - sample.rightX * sideOffset2,
        sample.surfaceY,
        z - sample.rightZ * sideOffset2,
        baseScale,
        rand
      );
    }

    for (let z = 260; z < maxZ; z += 140 + rand() * 80) {
      const sample = slope.sampleAtZ(z);
      const lateralMag = 3.6 + rand() * 1.8;
      const sign = rand() > 0.5 ? 1 : -1;
      const lateral = sign * lateralMag;
      this.spawnInstance(
        sample.centerX + sample.rightX * lateral,
        sample.surfaceY,
        z + sample.rightZ * lateral,
        baseScale * (0.85 + rand() * 0.3),
        rand
      );
    }
  }

  private spawnInstance(
    x: number,
    y: number,
    z: number,
    baseScale: number,
    rand: () => number
  ): void {
    const sizeFactor = TREE_SCALE_MIN + rand() * (TREE_SCALE_MAX - TREE_SCALE_MIN);
    const scale = baseScale * sizeFactor;
    const node = new TransformNode(`tree-${this.placements.length}`, this.scene);
    const lift = -this.templateMinY * scale;
    node.position = new Vector3(x, y + lift, z);
    node.rotation = new Vector3(0, rand() * Math.PI * 2, 0);
    node.scaling = new Vector3(scale, scale, scale);
    node.parent = this.root;

    for (const template of this.templates) {
      const inst = template.createInstance(`tree-inst-${this.placements.length}`);
      inst.parent = node;
      inst.isPickable = false;
    }

    this.placements.push({
      position: new Vector3(x, y, z),
      radius: TREE_COLLISION_RADIUS * sizeFactor
    });
  }

  hits(point: Vector3, riderRadius: number): boolean {
    for (const t of this.placements) {
      const dx = point.x - t.position.x;
      const dz = point.z - t.position.z;
      const dy = point.y - t.position.y;
      if (dy > 5 || dy < -2) continue;
      const r = t.radius + riderRadius;
      if (dx * dx + dz * dz < r * r) return true;
    }
    return false;
  }

  private computeTemplateBounds(): { height: number; minY: number } {
    let height = 0;
    let minY = Infinity;
    for (const t of this.templates) {
      t.computeWorldMatrix(true);
      const info = t.getBoundingInfo().boundingBox;
      const h = info.maximumWorld.y - info.minimumWorld.y;
      if (h > height) height = h;
      if (info.minimumWorld.y < minY) minY = info.minimumWorld.y;
    }
    return { height, minY: Number.isFinite(minY) ? minY : 0 };
  }
}

function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
